import React from 'react';

/**
 * `Fill →` — a WEEK GRID feature, on every editable grid (owner, 2026-10-25:
 * the unit timecard first, then "My timesheet should also have the fill
 * options?" — so it lives here, beside the grid, not in one caller).
 *
 * STEPPED, and its state is DERIVED FROM THE CELLS, never a counter (owner:
 * "clicking fill should fill 5 days, mon to fri. clicking fill again should
 * fill sat and clicking again should fill sun", then "the word fill changes
 * to Del maybe in red so clicking again removes the entire fill"):
 *   `Fill →` Tue–Fri → `+ Sat` → `+ Sun` → a red `Clear` that blanks what Fill
 *   fills (Monday, the source, stays). Day # numbers the whole week in ONE
 *   click (+1 a day, zero-padding kept, a Rest day skipped), then `Clear`.
 * 🔒 A cell the viewer may not edit (a buyer's 🔒 value on My timesheet, a
 * locked week) is SKIPPED by both Fill and Clear and counts as done, so it can
 * never hold the step back. Every write goes through the grid's own
 * `onCommit` — a DRAFT, saved by Save; a value equal to what the server holds
 * drops out of the draft, as a typed one does.
 */

const sameRaw = (a, b) => String(a == null ? '' : a) === String(b == null ? '' : b);
const isMixed = (c) => c.text === 'Mixed' && !c.drafted;

/** The rows Fill applies to: typed and picked values — never computed money, never free-text notes. */
export const FILLABLE_KINDS = Object.freeze(['time', 'hours', 'count', 'select', 'tick']);

function dayNumberTargets(monRaw, dates, getCell) {
  const text = String(monRaw == null ? '' : monRaw).trim();
  if (!/^\d+$/.test(text)) return [];
  const width = text.startsWith('0') ? text.length : 0;
  let n = Number(text);
  const out = [];
  for (const date of dates.slice(1)) {
    if (getCell({ key: 'day_type', kind: 'select' }, date).raw === 'rest') continue;
    n += 1;
    out.push([date, String(n).padStart(width, '0')]);
  }
  return out;
}

/** What the row's Fill link does next: `{ kind: 'none' | 'fill' | 'clear', … }`. */
export function fillPlan(row, dates, getCell) {
  // A CALCULATED row (calculated overtime, 2026-10-26) is never filled — Fill never writes an override.
  if (!FILLABLE_KINDS.includes(row.kind) || row.noFill) return { kind: 'none' };
  const mon = getCell(row, dates[0]);
  // `inherited`: a blank In/Out SHOWING the unit's call/wrap — nothing of Monday's own to copy.
  if (mon.text == null || isMixed(mon) || mon.clearing || mon.inherited) return { kind: 'none' };
  // A cell the viewer cannot edit — or an AUTO wrap (`fillSkip`, owner ruling 2A 2026-10-29: "Fill
  // skips auto wraps", so the day keeps following its call) — is skipped and counts as done.
  const editable = (d) => { const c = getCell(row, d); return !!c.editable && !c.fillSkip; };
  const holds = (d, v) => {
    const c = getCell(row, d);
    return !editable(d) || (!isMixed(c) && sameRaw(c.raw, v));
  };
  const writes = (pairs) => pairs.filter(([d]) => editable(d));
  if (row.key === 'day_number') {
    const targets = dayNumberTargets(mon.raw, dates, getCell);
    const mine = writes(targets);
    if (mine.length === 0) return { kind: 'none' };
    return targets.every(([d, v]) => holds(d, v))
      ? (() => { const c = mine.map(([d]) => d).filter((d) => getCell(row, d).clearable !== false); return c.length ? { kind: 'clear', dates: c } : { kind: 'none', done: true }; })()
      : { kind: 'fill', label: 'Fill →', title: 'Number the week from Monday (a Rest day is skipped)', writes: mine };
  }
  const m = mon.raw;
  const name = String(row.label).toLowerCase();
  const steps = [
    ['Fill →', `Copy Monday's ${name} to Tue–Fri`, dates.slice(1, 5)],
    ['+ Sat', `Copy Monday's ${name} to Saturday`, [dates[5]]],
    ['+ Sun', `Copy Monday's ${name} to Sunday`, [dates[6]]],
  ];
  if (writes(dates.slice(1).map((d) => [d, m])).length === 0) {
    // Monday HAS a value; if what blocks every target is an auto wrap (2A), say that — not "Enter Monday first".
    const auto = dates.slice(1).some((d) => getCell(row, d).fillSkip);
    return auto ? { kind: 'none', reason: 'auto' } : { kind: 'none' };
  }
  for (const [label, title, ds] of steps) {
    if (!ds.every((d) => holds(d, m))) return { kind: 'fill', label, title, writes: writes(ds.map((d) => [d, m])) };
  }
  // Everything already holds Monday's value: Clear only what a Clear can blank —
  // a cell that is not merely INHERITED (`clearable`, default true). Nothing to
  // clear (e.g. a whole row following the unit) → no link at all.
  const clearable = dates.slice(1).filter((d) => editable(d) && getCell(row, d).clearable !== false);
  return clearable.length ? { kind: 'clear', dates: clearable } : { kind: 'none', done: true };
}

export function runFill(row, plan, onCommit) {
  if (plan.kind === 'fill') for (const [d, v] of plan.writes) onCommit(row, d, v);
  if (plan.kind === 'clear') for (const d of plan.dates) onCommit(row, d, row.kind === 'tick' ? false : '');
}

const FILL_NONE_AUTO_TITLE = 'Nothing to fill — the other days work their wrap out from their own call';

const FILL_LINK =
  'min-h-[44px] md:min-h-0 whitespace-nowrap px-1 text-xs font-medium text-slate-500 group-hover:text-blue-700 hover:underline ' +
  'focus:text-blue-700 disabled:cursor-default disabled:opacity-50 disabled:no-underline dark:text-slate-400 ' +
  'dark:group-hover:text-blue-300 dark:focus:text-blue-300';
const CLEAR_LINK =
  'min-h-[44px] md:min-h-0 whitespace-nowrap px-1 text-xs font-medium text-red-600 hover:underline focus:underline dark:text-red-400';

/**
 * The row's link. Muted at rest (slate, not gray: `index.css` forces dark
 * `text-gray-*` with !important, which would also kill the hover blue), blue
 * on row hover / focus; `Clear` is the house red pair.
 */
export function FillLink({ row, dates, getCell, onCommit, idPrefix }) {
  const plan = fillPlan(row, dates, getCell);
  const testId = `${idPrefix}-fill-${row.key}`;
  if (plan.kind === 'clear') {
    return (
      <button type="button" data-testid={testId} data-fill-step="clear" className={CLEAR_LINK}
        title={row.key === 'day_number' ? 'Remove the filled numbers — Monday stays' : 'Remove the filled values Tue–Sun — Monday stays'}
        onClick={() => runFill(row, plan, onCommit)}>
        Clear
      </button>
    );
  }
  if (plan.done) return null;
  const can = plan.kind === 'fill';
  return (
    <button type="button" data-testid={testId} data-fill-step={can ? plan.label : 'none'} disabled={!can} className={FILL_LINK}
      title={can ? plan.title : plan.reason === 'auto' ? FILL_NONE_AUTO_TITLE : 'Enter Monday first'} onClick={() => runFill(row, plan, onCommit)}>
      {can ? plan.label : 'Fill →'}
    </button>
  );
}
